import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowLeft, Youtube, ArrowRight } from 'lucide-react';
import { api } from '../lib/api';
import Newsletter from '../components/Newsletter';
import ShareButton from '../components/ShareButton';
import ReflectionAudioPlayer from '../components/ReflectionAudioPlayer';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SPRING = { type: 'spring', stiffness: 700, damping: 22, mass: 0.6 };

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [episode, setEpisode] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null);
    setEpisode(null);
    setNotFound(false);
    api.get(`/posts/${slug}`).then((res) => setPost(res.data)).catch(() => setNotFound(true));
    api.get(`/posts/${slug}/episode`).then((res) => setEpisode(res.data)).catch(() => setEpisode(null));
  }, [slug]);

  // Update social share meta tags so crawlers (with SSR/prerender) can see the OG image.
  useEffect(() => {
    if (!post) return undefined;
    const ogImg = post.cover_image ? `${BACKEND}/api/og/post/${post.slug}.png` : `${window.location.origin}/og-default.png`;
    const canonicalUrl = `${window.location.origin}/blog/${post.slug}`;
    const setMeta = (name, content, attr = 'name') => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    const prevTitle = document.title;
    document.title = `${post.title} · AI Revolution Digest`;
    setMeta('description', post.excerpt || '');
    setMeta('og:title', post.title, 'property');
    setMeta('og:description', post.excerpt || '', 'property');
    setMeta('og:image', ogImg, 'property');
    setMeta('og:type', 'article', 'property');
    setMeta('og:url', canonicalUrl, 'property');
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', post.title);
    setMeta('twitter:description', post.excerpt || '');
    setMeta('twitter:image', ogImg);
    return () => { document.title = prevTitle; };
  }, [post]);

  if (notFound) {
    return (
      <div className="pt-40 pb-20 text-center">
        <h1 className="text-3xl font-bold mb-4">Post not found</h1>
        <Link to="/episodes" className="text-[#EF5B5B]">← Back to Episodes</Link>
      </div>
    );
  }

  if (!post) return <div className="pt-40 pb-20 text-center text-gray-500">Loading...</div>;

  return (
    <article className="pt-28 pb-16 px-4 sm:px-6" data-testid="blog-post-page">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <Link to="/episodes" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-[#EF5B5B] smooth">
            <ArrowLeft className="w-4 h-4" /> Back to Episodes
          </Link>
          <ShareButton
            url={typeof window !== 'undefined' ? window.location.href : ''}
            title={post.title}
            testId={`blog-post-share-${post.slug}`}
          />
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="flex items-center gap-3 mb-5">
            <span className="px-3 py-1 rounded-full bg-[#EF5B5B] text-white text-xs font-semibold">{post.category}</span>
            <span className="text-sm text-gray-500">{new Date(post.published_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 leading-tight">{post.title}</h1>
          <p className="text-sm text-gray-400 mb-6">By {post.author}</p>

          <ReflectionAudioPlayer slug={post.slug} />

          {post.cover_image && (
            <div className="rounded-3xl overflow-hidden mb-16 sm:mb-20 glow-red" data-testid="post-cover-image">
              <img src={post.cover_image} alt={post.title} className="w-full object-cover" />
            </div>
          )}
        </motion.div>

        <div
          data-testid="post-narrative"
          className="reflection-prose text-gray-200 text-[17px] sm:text-lg text-left mx-auto"
          style={{
            maxWidth: '800px',
            paddingLeft: '40px',
            paddingRight: '40px',
            lineHeight: 1.6,
          }}
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content}</ReactMarkdown>
        </div>

        {/* Launchpad — listen or watch the full episode */}
        {episode && (episode.youtube_id || episode.apple_url || episode.spotify_url) && (
          <motion.section
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ type: 'spring', stiffness: 520, damping: 26 }}
            className="mt-14 sm:mt-16 rounded-3xl border border-[#EF5B5B]/30 overflow-hidden"
            data-testid="episode-launchpad"
            style={{
              background:
                'radial-gradient(120% 90% at 0% 0%, rgba(239,91,91,0.14), transparent 55%),' +
                'linear-gradient(180deg, rgba(20,10,12,0.85), rgba(10,10,10,0.92))',
              boxShadow: '0 0 0 1px rgba(239,91,91,0.2), 0 20px 60px -20px rgba(239,91,91,0.45)',
            }}
          >
            <div className="p-8 sm:p-10 text-center">
              <p className="text-[10px] sm:text-xs tracking-[0.3em] uppercase text-[#EF5B5B] font-black mb-3">
                Watch or Listen to the Full Episode
              </p>
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-8 leading-snug">
                {episode.title}
              </h3>

              <div className="flex flex-col items-center gap-4">
                {episode.youtube_id && (
                  <motion.a
                    href={`https://www.youtube.com/watch?v=${episode.youtube_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="launchpad-youtube"
                    whileHover={{ scale: 1.04, y: -2 }}
                    whileTap={{ scale: 0.97 }}
                    transition={SPRING}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-10 py-4 btn-red rounded-full font-bold text-base sm:text-lg"
                  >
                    <Youtube className="w-6 h-6" />
                    Watch on YouTube
                    <ArrowRight className="w-5 h-5" />
                  </motion.a>
                )}

                {(episode.spotify_url || episode.apple_url) && (
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto pt-2">
                    {episode.spotify_url && (
                      <motion.a
                        href={episode.spotify_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="launchpad-spotify"
                        whileHover={{ scale: 1.04 }}
                        whileTap={{ scale: 0.97 }}
                        transition={SPRING}
                        className="neon-hover inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border-2 border-[#EF5B5B]/40 hover:border-[#EF5B5B] text-[#EF5B5B] font-semibold text-sm smooth"
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                          <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" />
                        </svg>
                        Listen on Spotify
                      </motion.a>
                    )}
                    {episode.apple_url && (
                      <motion.a
                        href={episode.apple_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="launchpad-apple"
                        whileHover={{ scale: 1.04 }}
                        whileTap={{ scale: 0.97 }}
                        transition={SPRING}
                        className="neon-hover inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border-2 border-[#EF5B5B]/40 hover:border-[#EF5B5B] text-[#EF5B5B] font-semibold text-sm smooth"
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                          <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 2.182c5.423 0 9.818 4.395 9.818 9.818 0 5.423-4.395 9.818-9.818 9.818-5.423 0-9.818-4.395-9.818-9.818 0-5.423 4.395-9.818 9.818-9.818zM12 5.455c-1.767 0-3.273 1.506-3.273 3.272 0 1.767 1.506 3.273 3.273 3.273s3.273-1.506 3.273-3.273c0-1.766-1.506-3.272-3.273-3.272zm0 4.364c-2.423 0-4.364 1.94-4.364 4.363v2.182c0 .402.326.727.728.727h7.272c.402 0 .728-.325.728-.727v-2.182c0-2.423-1.941-4.363-4.364-4.363z" />
                        </svg>
                        Listen on Apple Podcasts
                      </motion.a>
                    )}
                  </div>
                )}
              </div>
            </div>
          </motion.section>
        )}

        {post.tags && post.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-14 pt-10 border-t border-white/10">
            {post.tags.map((t, i) => (
              <span key={i} className="px-3 py-1 rounded-full glass text-sm text-gray-300">#{t}</span>
            ))}
          </div>
        )}
      </div>
      <Newsletter />
    </article>
  );
};

export default BlogPostPage;
